import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageShell from "@/components/brand/PageShell";
import { api } from "@/lib/api";
import { useAuth, dashboardPathForRole } from "@/context/AuthContext";

/**
 * /verify-email
 *
 * Two flows converge on this page:
 *   1. Just registered — Signup.jsx navigates here with ?email=<address>
 *      and NO token. We show "check your inbox" copy. Nothing to do until
 *      the user clicks the link inside the Resend email.
 *   2. Clicked the verification link from the delivered email — the URL
 *      carries ?token=<uuid>. We auto-verify on mount, sign the user in,
 *      and redirect to their role dashboard.
 */
export default function VerifyEmail() {
    const [params] = useSearchParams();
    const initialToken = params.get("token") || "";
    const initialEmail = params.get("email") || "";
    const [status, setStatus] = useState(initialToken ? "verifying" : "idle");
    const [message, setMessage] = useState("");
    const { signIn } = useAuth();
    const navigate = useNavigate();

    async function runVerify(t) {
        setStatus("verifying");
        setMessage("");
        try {
            const res = await api.verifyEmail(t);
            signIn(res.user);
            setStatus("success");
            setMessage("Email verified. Redirecting…");
            setTimeout(() => {
                navigate(dashboardPathForRole(res.user.role), { replace: true });
            }, 400);
        } catch (err) {
            setStatus("error");
            setMessage(err.message || "Verification failed.");
        }
    }

    useEffect(() => {
        if (initialToken) runVerify(initialToken);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialToken]);

    const hasToken = Boolean(initialToken);

    return (
        <PageShell>
            <section className="max-w-md mx-auto px-6 py-16" data-testid="verify-email-page">
                <div className="st-surface p-8">
                    <h1
                        className="font-serif text-3xl"
                        style={{ color: "var(--st-navy)" }}
                        data-testid="verify-heading"
                    >
                        Verify your email
                    </h1>

                    <p
                        className="mt-4 text-base leading-relaxed"
                        style={{ color: "var(--st-ink)" }}
                        data-testid="verify-email-copy"
                    >
                        {initialEmail ? (
                            <>
                                We've sent a verification link to{" "}
                                <strong style={{ color: "var(--st-navy)" }}>{initialEmail}</strong>.
                                Check your inbox and spam folder.
                            </>
                        ) : (
                            <>
                                We've sent a verification link to your email address.
                                Check your inbox and spam folder.
                            </>
                        )}
                    </p>

                    {/* Auto-verify feedback: only shown when a ?token= was present
                        in the URL (i.e. the user clicked the email link). */}
                    {hasToken && status === "verifying" && (
                        <div
                            className="mt-6 text-sm px-3 py-2 rounded"
                            style={{ background: "#EFF4FF", color: "#1E3A8A", border: "1px solid #C7D2FE" }}
                            data-testid="verify-pending"
                        >
                            Verifying your email…
                        </div>
                    )}
                    {hasToken && status === "success" && (
                        <div
                            className="mt-6 text-sm px-3 py-2 rounded"
                            style={{ background: "#ECFDF3", color: "#067647", border: "1px solid #ABEFC6" }}
                            data-testid="verify-success"
                        >
                            {message}
                        </div>
                    )}
                    {hasToken && status === "error" && (
                        <div
                            className="mt-6 text-sm px-3 py-2 rounded"
                            style={{ background: "#FEF1F2", color: "#B42318", border: "1px solid #FECDCA" }}
                            data-testid="verify-error"
                        >
                            {message}
                        </div>
                    )}

                    <p className="mt-8 text-xs" style={{ color: "var(--st-silver-500)" }}>
                        Return to <Link to="/login" className="link-royal">Sign in</Link>.
                    </p>
                </div>
            </section>
        </PageShell>
    );
}
